import React, { useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { schemas } from '@recurrency/core-api-schema';
import { Button, Form, Modal, notification } from 'antd';
import moment, { Moment } from 'moment';

import { Alert } from 'components/Alert';
import { IsoDatePicker } from 'components/DatePicker/IsoDatePicker';
import { FlexSpace } from 'components/FlexSpace';

import { coreApiFetch } from 'utils/api';
import { captureAndShowError } from 'utils/error';

export type ChangeSyncDateType = 'Modified' | 'Updated';

export interface SyncDatesData {
  dateType: ChangeSyncDateType;
  initialDate: string;
  tenantId: string;
  tableName: string;
  dbType: string;
  onDataChanged: () => void;
}

export interface EditSyncDatesModalProps {
  syncDatesData: SyncDatesData;
  onClose: () => void;
}

export const EditSyncDatesModal = ({ syncDatesData, onClose }: EditSyncDatesModalProps) => {
  const { dateType, initialDate, tenantId, tableName, dbType, onDataChanged } = syncDatesData;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSaveDate = async (values: { date: string }) => {
    try {
      setIsSubmitting(true);

      const bodyParams =
        dateType === 'Modified'
          ? {
              tableName,
              dbType,
              lastModified: values.date,
            }
          : {
              tableName,
              dbType,
              updatedAt: values.date,
            };

      await coreApiFetch(schemas.integrations.updateTenantTableSyncDates, {
        pathParams: { tenantId },
        bodyParams,
      });

      notification.success({
        message: 'Changes saved successfully.',
      });
      onDataChanged();
      onClose();
    } catch (err) {
      captureAndShowError(err, 'Error saving changes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible
      title={`Edit ${dateType} Date for ${tableName}`}
      onCancel={onClose}
      footer={
        <div>
          <Button onClick={onClose}>Cancel</Button>
          <Button key="submit" type="primary" form="form" htmlType="submit" loading={isSubmitting}>
            Save
          </Button>
        </div>
      }
      centered
    >
      <FlexSpace gap={20} direction="column">
        <Alert
          icon={<ExclamationCircleOutlined />}
          message={
            {
              Updated:
                "This will change the 'updated_at' field in public.date_last_sync_info, causing sync changes for Typesense.",
              Modified:
                "This will change the 'date_last_modified' field in public.date_last_sync_info, causing sync changes for Postgres.",
            }[dateType]
          }
          type="info"
          banner
        />

        <Form name="form" initialValues={{ date: initialDate }} onFinish={onSaveDate} layout="vertical">
          <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Date is required' }]}>
            <IsoDatePicker
              showTime
              disabledDates={(date: Moment) =>
                // allow all historical dates
                date && date > moment().startOf('day')
              }
            />
          </Form.Item>
        </Form>
      </FlexSpace>
    </Modal>
  );
};
